import * as React from 'react';
import { UIHeading1, UIHeading2, UIHeading4 } from '@cian/ui-kit';

import { useFloatingHeader } from './hooks/useFloatingHeader';
import { HeaderRow } from './parts/HeaderRow';
import { ScrollButtons } from './parts/ScrollButtons';
import * as style from './style.css';

const COLUMN_WIDTH = 320;
const COLUMN_GAP = 16;
const COLUMN_STRIDE = COLUMN_WIDTH + COLUMN_GAP;

export interface INewbuildingsCompareBlockRow {
  kind: 'block';
  key: string;
  title: string;
}

export interface INewbuildingsCompareDataRow {
  kind: 'data';
  key: string;
  title?: string | null;
  cells: React.ReactNode[];
}

export type TNewbuildingsCompareViewRow = INewbuildingsCompareBlockRow | INewbuildingsCompareDataRow;

export interface INewbuildingsComparePageProps {
  title: string;
  headers: React.ReactNode[];
  floatingHeaders: React.ReactNode[];
  rows: TNewbuildingsCompareViewRow[];
}

export const NewbuildingsComparePage = ({ title, headers, floatingHeaders, rows }: INewbuildingsComparePageProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const floatingHeaderScrollRef = React.useRef<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement | null>(null);
  const dragStartRef = React.useRef({ scrollLeft: 0, x: 0 });
  const [dragging, setDragging] = React.useState(false);
  const { isFloatingHeaderMounted, isFloatingHeaderVisible, syncFloatingHeaderScroll } = useFloatingHeader({
    floatingHeaderScrollRef,
    headerRowRef,
    scrollRef,
  });

  const scrollByColumn = (direction: 'left' | 'right') => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    const { scrollLeft } = scrollElement;
    const target =
      direction === 'right'
        ? (Math.floor(scrollLeft / COLUMN_STRIDE) + 1) * COLUMN_STRIDE
        : Math.max(0, (Math.ceil(scrollLeft / COLUMN_STRIDE) - 1) * COLUMN_STRIDE);

    scrollElement.scrollTo({ behavior: 'smooth', left: target });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    scrollByColumn(event.key === 'ArrowLeft' ? 'left' : 'right');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    syncFloatingHeaderScroll(event.currentTarget.scrollLeft);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !scrollRef.current) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      scrollLeft: scrollRef.current.scrollLeft,
      x: event.clientX,
    };
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !scrollRef.current) {
      return;
    }

    const distance = event.clientX - dragStartRef.current.x;
    scrollRef.current.scrollLeft = dragStartRef.current.scrollLeft - distance;
    syncFloatingHeaderScroll(scrollRef.current.scrollLeft);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setDragging(false);
  };

  return (
    <div className={style['container']} data-testid="newbuildings-compare_page">
      <div className={style['title']}>
        <UIHeading1 as="h1">{title}</UIHeading1>
        <ScrollButtons onScroll={scrollByColumn} />
      </div>
      {isFloatingHeaderMounted && (
        <div
          className={isFloatingHeaderVisible ? style['floating-header-visible'] : style['floating-header']}
          data-testid="FloatingHeader"
        >
          <div ref={floatingHeaderScrollRef} className={style['floating-header-scroller']}>
            <div className={style['table']} role="table">
              <HeaderRow floating headers={floatingHeaders} />
            </div>
          </div>
          <ScrollButtons floating onScroll={scrollByColumn} />
        </div>
      )}
      <div
        ref={scrollRef}
        className={dragging ? style['table-scroller-dragging'] : style['table-scroller']}
        data-testid="newbuildings-compare_table"
        onKeyDown={handleKeyDown}
        onPointerCancel={handlePointerEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onScroll={handleScroll}
        role="region"
        tabIndex={0}
      >
        <div className={style['table']} role="table">
          <HeaderRow headers={headers} rowRef={headerRowRef} />
          {rows.map(row =>
            row.kind === 'block' ? (
              <div className={style['row']} key={row.key} role="row">
                <div className={style['block-cell']} role="cell">
                  <UIHeading2>{row.title}</UIHeading2>
                </div>
              </div>
            ) : (
              <div className={style['row']} key={row.key} role="row">
                <div className={style['cell']} role="cell">
                  <UIHeading4>{row.title}</UIHeading4>
                </div>
                {row.cells.map((cell, cellIndex) => (
                  <div className={style['cell']} key={cellIndex} role="cell">
                    {cell}
                  </div>
                ))}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};
